import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import {
  Calendar,
  Users,
  MapPin,
  Clock,
  Phone,
  Mail,
  Building2,
  CheckCircle2,
  FileText,
  Plane,
  Hotel,
  Car,
  Ship,
  Utensils,
  Ticket,
  DollarSign
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

// Voyager Pro / Figma reskin - 'agency' (Modern Agency / Corporate Timeline)
// is a dark slate theme in Figma with a sky-blue accent, replacing the
// previous light slate/gray look. Full light->dark rebuild per user direction.
const PALETTE = TEMPLATE_PALETTES.modern_agency;

const ModernAgencyTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getComponentIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    const style = { color: 'var(--accent)' };

    if (type.includes('flight') || title.includes('flight') || title.includes('air')) {
      return <Plane className="w-5 h-5" style={style} />;
    } else if (type.includes('hotel') || title.includes('hotel') || title.includes('resort')) {
      return <Hotel className="w-5 h-5" style={style} />;
    } else if (type.includes('car') || title.includes('car') || title.includes('rental')) {
      return <Car className="w-5 h-5" style={style} />;
    } else if (type.includes('cruise') || title.includes('cruise') || title.includes('ship')) {
      return <Ship className="w-5 h-5" style={style} />;
    } else if (type.includes('restaurant') || type.includes('dining') || title.includes('dining')) {
      return <Utensils className="w-5 h-5" style={style} />;
    } else if (type.includes('activity') || type.includes('ticket') || type.includes('park')) {
      return <Ticket className="w-5 h-5" style={style} />;
    }
    return <MapPin className="w-5 h-5" style={style} />;
  };

  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Valued Client';
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Header Section - Premium Agency Branding */}
      <div style={{ backgroundColor: 'var(--muted)', borderBottom: '2px solid var(--accent)' }} className="shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold mb-2" style={{ color: 'var(--accent)' }}>
                <Building2 className="w-4 h-4" />
                <span>PROFESSIONAL TRAVEL SERVICES</span>
              </div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--foreground)' }}>
                {quote.quoteTitle || 'Your Travel Itinerary'}
              </h1>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--muted-foreground)' }}>Quote Reference</div>
              <div className="font-mono text-sm px-3 py-1 rounded" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                {quote.quoteNumber}
              </div>
            </div>
          </div>

          {/* Client & Trip Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 mt-0.5" style={{ color: 'var(--accent)' }} />
              <div>
                <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Traveler</div>
                <div className="font-semibold" style={{ color: 'var(--foreground)' }}>{getClientName()}</div>
              </div>
            </div>

            {quote.travelDates?.start && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 mt-0.5" style={{ color: 'var(--accent)' }} />
                <div>
                  <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Travel Dates</div>
                  <div className="font-semibold" style={{ color: 'var(--foreground)' }}>
                    {formatDate(quote.travelDates.start)}
                    {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                  </div>
                </div>
              </div>
            )}

            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 mt-0.5" style={{ color: 'var(--accent)' }} />
                <div>
                  <div className="text-xs uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Party Size</div>
                  <div className="font-semibold" style={{ color: 'var(--foreground)' }}>
                    {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Vertical Timeline */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Your Itinerary</h2>
          <p style={{ color: 'var(--muted-foreground)' }}>A detailed breakdown of your travel arrangements</p>
        </div>

        {/* Timeline Container */}
        {quote.components && quote.components.length > 0 ? (
          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5" style={{ backgroundColor: 'var(--border)' }}></div>

            {/* Components as timeline items */}
            <div className="space-y-8">
              {quote.components.map((component, index) => (
                <div key={component.id || index} className="relative pl-16">
                  {/* Timeline dot */}
                  <div className="absolute left-3.5 top-6 w-5 h-5 rounded-full border-4 shadow-md" style={{ backgroundColor: 'var(--accent)', borderColor: 'var(--background)' }}></div>

                  {/* Component Card */}
                  <div className="rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
                    {/* Card Header */}
                    <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          {getComponentIcon(component)}
                          <div>
                            <h3 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>
                              {component.title || `Component ${index + 1}`}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-block text-xs font-semibold px-2 py-1 rounded" style={{ color: 'var(--accent)', backgroundColor: 'var(--background)' }}>
                                {component.type || 'General'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-auto">
                          <div className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Component Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || key === 'isOption' || key === 'optionDescription') return null;

                            const isPriceField = key.toLowerCase().includes('price') ||
                                                 key.toLowerCase().includes('cost') ||
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            const isConfirmation = key.toLowerCase().includes('confirmation') ||
                                                   key.toLowerCase().includes('reference') ||
                                                   key.toLowerCase().includes('booking');

                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }

                            return (
                              <div key={key} className="flex flex-col">
                                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--muted-foreground)' }}>
                                  {humanizeKey(key)}
                                </div>
                                <div
                                  className={isConfirmation ? 'font-mono text-xs p-2 rounded' : 'font-semibold'}
                                  style={{ color: 'var(--foreground)', backgroundColor: isConfirmation ? 'var(--background)' : 'transparent', border: isConfirmation ? '1px solid var(--border)' : 'none' }}
                                >
                                  {displayValue}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {component.photos.map((photo, photoIndex) => (
                            <div
                              key={photoIndex}
                              className="relative group cursor-pointer rounded-lg overflow-hidden shadow hover:shadow-md transition-shadow"
                              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background)' }}
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img
                                src={photo.url}
                                alt={photo.name || 'Component photo'}
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                <span className="text-white text-sm font-medium">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a
                              key={docIndex}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-2 rounded transition-colors"
                              style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                            >
                              <span className="text-lg">📄</span>
                              <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasMultipleOptions(component) && (
                      <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {getComponentOptions(component).map((option, optIndex) => (
                            <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                              {option.photoUrl && (
                                <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                              )}
                              <div className="p-4">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                  {option.badge && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                )}
                                <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-xl shadow-sm p-12 text-center" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
            <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
            <p style={{ color: 'var(--muted-foreground)' }}>No components added to this itinerary yet</p>
          </div>
        )}

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-12 rounded-xl p-6" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 mt-0.5" style={{ color: 'var(--accent)' }} />
              <div>
                <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--foreground)' }}>Important Information</h3>
                <p className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {quote.notes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Summary - Professional Box */}
        <div className="mt-12 rounded-xl shadow-md overflow-hidden" style={{ backgroundColor: 'var(--muted)', border: '2px solid var(--border)' }}>
          <div className="px-6 py-4" style={{ backgroundColor: 'var(--primary)' }}>
            <h3 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
              <DollarSign className="w-5 h-5" />
              Investment Summary
            </h3>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
                <span className="font-medium" style={{ color: 'var(--muted-foreground)' }}>Total Investment</span>
                <span className="text-3xl font-bold" style={{ color: 'var(--foreground)' }}>
                  {formatCurrency(quote.totalPrice)}
                </span>
              </div>

              {quote.depositAmount > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="font-medium" style={{ color: 'var(--muted-foreground)' }}>Initial Deposit Required</span>
                    <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                      {formatCurrency(quote.depositAmount)}
                    </span>
                  </div>

                  {quote.depositDueDate && (
                    <div className="flex justify-between items-center text-sm">
                      <span style={{ color: 'var(--muted-foreground)' }}>Deposit Due Date</span>
                      <span className="font-semibold" style={{ color: 'var(--foreground)' }}>
                        {formatDate(quote.depositDueDate)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Button */}
            {onPayment && (
              <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl hover:brightness-110'
                  }`}
                  style={{ backgroundColor: 'var(--accent)', color: 'var(--background)' }}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Proceed to Secure Payment'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
                    Secure payment processing • Your information is protected
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 rounded-xl shadow-md overflow-hidden" style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--primary)' }}>
              <div className="flex items-center justify-center gap-2" style={{ color: 'var(--foreground)' }}>
                <Phone className="w-5 h-5" />
                <h3 className="text-lg font-bold">Your Travel Advisor</h3>
              </div>
            </div>

            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="font-medium" style={{ color: 'var(--muted-foreground)' }}>{quote.agent_business_name}</p>
                  )}
                </div>
              )}

              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                  >
                    <Mail className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_email}</span>
                  </a>
                )}

                {quote.agent_phone && (
                  <a
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                  >
                    <Phone className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer Branding */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <Building2 className="w-4 h-4" />
            <span>Professional Travel Services • Trusted Since Day One</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernAgencyTemplate;
